import { useMemo, useRef } from "react";
import type { POI } from "../../components/MapCanvas";
import type { CategoryForCarousel } from "../../components/visitor/CategoryCarousel";
import { createPOISearchIndex, searchPOIs } from "../../lib/poi-search";
import {
  matchesAnyOverlay,
  shouldDimPOI,
  countPOIsPerOverlay,
  type POIWithAccessibility,
} from "@wondermaps/maps";

interface MapCategory {
  slug: string;
  name: string;
  icon: string;
  color: string;
  sortOrder: number;
  isDefault?: boolean;
}

interface UseVisitorPOIFilteringOptions {
  pois: POI[];
  categories: MapCategory[];
  selectedCategory: string | null | undefined;
  debouncedSearchQuery: string;
  /** Set of bookmarked POI IDs for filtering */
  bookmarkedPoiIds?: Set<string>;
  /** When true, filter to only show bookmarked POIs */
  showSavedOnly?: boolean;
  /** Active overlay IDs for accessibility filtering */
  activeOverlayIds?: string[];
  /** Sub-filters per overlay (e.g., dietary: ["vegan"]) */
  overlaySubFilters?: Record<string, string[]>;
}

interface UseVisitorPOIFilteringResult {
  filteredPois: POI[];
  poiCounts: Record<string, number>;
  carouselCategories: CategoryForCarousel[];
  defaultCategory: string;
  fuseIndex: ReturnType<typeof createPOISearchIndex>;
  savedCount: number;
  /** Counts of POIs per overlay (for panel display) */
  overlayPoiCounts: Record<string, number>;
  /** POI IDs that should be dimmed (match no active overlay) */
  dimmedPoiIds: Set<string>;
}

/**
 * Convert POI to POIWithAccessibility format for overlay filtering
 */
function toPOIWithAccessibility(poi: POI): POIWithAccessibility {
  return {
    id: poi.id,
    categorySlug: poi.categorySlug,
    propertiesJson: poi.propertiesJson as POIWithAccessibility["propertiesJson"],
  };
}

/**
 * Handles POI filtering, counting, and search index for visitor map
 */
export function useVisitorPOIFiltering({
  pois,
  categories,
  selectedCategory,
  debouncedSearchQuery,
  bookmarkedPoiIds,
  showSavedOnly,
  activeOverlayIds = [],
  overlaySubFilters = {},
}: UseVisitorPOIFilteringOptions): UseVisitorPOIFilteringResult {
  // Default category from API
  const defaultCategory = useMemo(() => {
    return categories.find((c) => c.isDefault)?.slug ?? "all";
  }, [categories]);

  // Search index
  const fuseIndex = useMemo(() => createPOISearchIndex(pois), [pois]);

  // Count of saved/bookmarked POIs
  const savedCount = useMemo(() => {
    if (!bookmarkedPoiIds) return 0;
    // Count only bookmarked IDs that exist in current POI list
    return pois.filter((p) => bookmarkedPoiIds.has(p.id)).length;
  }, [pois, bookmarkedPoiIds]);

  // Count POIs per overlay (for panel display)
  const overlayPoiCounts = useMemo(() => {
    const accessibilityPois = pois.map(toPOIWithAccessibility);
    return countPOIsPerOverlay(accessibilityPois);
  }, [pois]);

  // Compute which POIs should be dimmed (no overlay match when overlays active)
  const dimmedPoiIds = useMemo(() => {
    if (activeOverlayIds.length === 0) {
      return new Set<string>();
    }
    const dimmed = new Set<string>();
    for (const poi of pois) {
      const accessibilityPoi = toPOIWithAccessibility(poi);
      if (shouldDimPOI(accessibilityPoi, activeOverlayIds, overlaySubFilters)) {
        dimmed.add(poi.id);
      }
    }
    return dimmed;
  }, [pois, activeOverlayIds, overlaySubFilters]);

  // Filter POIs by category, search, and overlays
  // Use ref to stabilize output — return cached array when POI IDs haven't changed
  // Prevents MapCanvas fade animation from firing on unrelated re-renders
  const prevFilteredRef = useRef<POI[]>([]);
  const prevFilteredIdsRef = useRef<string>("");

  const filteredPois = useMemo(() => {
    // First filter by bookmarks if showSavedOnly is true
    let filtered = showSavedOnly && bookmarkedPoiIds
      ? pois.filter((p) => bookmarkedPoiIds.has(p.id))
      : pois;

    // Then apply category filter (skip if showing saved only)
    if (!showSavedOnly) {
      filtered =
        selectedCategory === "all" || selectedCategory === undefined || selectedCategory === null
          ? filtered
          : filtered.filter((p) => p.categorySlug === selectedCategory);
    }

    // Apply overlay filter if any overlays are active
    // Note: We don't filter out non-matching POIs, we dim them instead
    // But for list view, we DO want to show only matching POIs
    if (activeOverlayIds.length > 0) {
      filtered = filtered.filter((poi) => {
        const accessibilityPoi = toPOIWithAccessibility(poi);
        return matchesAnyOverlay(accessibilityPoi, activeOverlayIds, overlaySubFilters);
      });
    }

    // Apply search filter
    if (debouncedSearchQuery.length >= 2) {
      const searchResults = searchPOIs(fuseIndex, debouncedSearchQuery);
      const filteredIds = new Set(filtered.map((p) => p.id));
      filtered = searchResults.filter((p) => filteredIds.has(p.id));
    }

    // Stabilize: if same POI IDs in same order, return previous reference
    const idsKey = filtered.map((p) => p.id).join(",");
    if (idsKey === prevFilteredIdsRef.current) {
      return prevFilteredRef.current;
    }
    prevFilteredIdsRef.current = idsKey;
    prevFilteredRef.current = filtered;
    return filtered;
  }, [pois, selectedCategory, debouncedSearchQuery, fuseIndex, bookmarkedPoiIds, showSavedOnly, activeOverlayIds, overlaySubFilters]);

  // POI counts per category
  const poiCounts = useMemo(() => {
    return pois.reduce<Record<string, number>>((acc, poi) => {
      if (poi.categorySlug) {
        acc[poi.categorySlug] = (acc[poi.categorySlug] ?? 0) + 1;
      }
      return acc;
    }, {});
  }, [pois]);

  // Categories formatted for carousel
  const carouselCategories: CategoryForCarousel[] = useMemo(() => {
    return categories.map((c) => ({
      slug: c.slug,
      name: c.name,
      icon: c.icon,
      color: c.color,
      sortOrder: c.sortOrder,
    }));
  }, [categories]);

  return {
    filteredPois,
    poiCounts,
    carouselCategories,
    defaultCategory,
    fuseIndex,
    savedCount,
    overlayPoiCounts,
    dimmedPoiIds,
  };
}
